import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Sparkles, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import FrequencySelector from "@/components/FrequencySelector";
import AudioPlayer from "@/components/AudioPlayer";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const voices = [
  { value: "shimmer", label: "Shimmer", desc: "Bright, cheerful" },
  { value: "nova", label: "Nova", desc: "Energetic, upbeat" },
  { value: "echo", label: "Echo", desc: "Smooth, calm" },
  { value: "onyx", label: "Onyx", desc: "Deep, authoritative" },
  { value: "alloy", label: "Alloy", desc: "Neutral, balanced" },
  { value: "fable", label: "Fable", desc: "Expressive, storytelling" },
];

const Creator = () => {
  const [title, setTitle] = useState("");
  const [affirmations, setAffirmations] = useState([""]);
  const [frequency, setFrequency] = useState(432);
  const [loopDuration, setLoopDuration] = useState(30);
  const [voice, setVoice] = useState("shimmer");
  const [speed, setSpeed] = useState(0.5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedAudio, setGeneratedAudio] = useState(null);

  const addAffirmation = () => {
    if (affirmations.length < 20) {
      setAffirmations([...affirmations, ""]);
    }
  };

  const removeAffirmation = (index) => {
    if (affirmations.length > 1) {
      setAffirmations(affirmations.filter((_, i) => i !== index));
    }
  };

  const updateAffirmation = (index, value) => {
    const updated = [...affirmations];
    updated[index] = value;
    setAffirmations(updated);
  };

  const handleGenerate = async () => {
    const validAffirmations = affirmations.filter(a => a.trim());
    
    if (!title.trim()) {
      toast.error("Please enter a title for your subliminal");
      return;
    }
    
    if (validAffirmations.length === 0) {
      toast.error("Please add at least one affirmation");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/subliminals`, {
        title: title.trim(),
        affirmations: validAffirmations,
        frequency,
        loop_duration: loopDuration,
        voice,
        speed
      });
      
      setGeneratedAudio(response.data);
      toast.success("Subliminal created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate subliminal. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setTitle("");
    setAffirmations([""]);
    setFrequency(432);
    setLoopDuration(30);
    setVoice("shimmer");
    setSpeed(0.5);
    setGeneratedAudio(null);
  };

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Create Your <span className="text-neon">Subliminal</span>
          </h1>
          <p className="text-gray-400">
            Write your affirmations and transform them into powerful audio
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-3 space-y-6">
            {/* Title */}
            <GlassCard hover={false}>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Morning Abundance Meditation"
                data-testid="subliminal-title"
                className="w-full px-4 py-3 bg-black/20 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
              />
            </GlassCard>

            {/* Affirmations */}
            <GlassCard hover={false}>
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-300">
                  Affirmations ({affirmations.length}/20)
                </label>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={addAffirmation}
                  disabled={affirmations.length >= 20}
                  data-testid="add-affirmation-btn"
                  className="flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300 disabled:opacity-50"
                >
                  <Plus size={16} />
                  Add
                </motion.button>
              </div>
              
              <div className="space-y-3">
                <AnimatePresence mode="popLayout">
                  {affirmations.map((aff, index) => (
                    <motion.div
                      key={index}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex items-center gap-2"
                    >
                      <span className="text-xs text-gray-600 w-6">{index + 1}.</span>
                      <input
                        type="text"
                        value={aff}
                        onChange={(e) => updateAffirmation(index, e.target.value)}
                        placeholder="I am worthy of abundance..."
                        data-testid={`affirmation-${index}`}
                        className="flex-1 px-4 py-2.5 bg-black/20 border border-white/10 rounded-lg text-white placeholder:text-gray-600 focus:border-violet-500/50 focus:outline-none text-sm"
                      />
                      {affirmations.length > 1 && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => removeAffirmation(index)}
                          data-testid={`remove-affirmation-${index}`}
                          className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <X size={16} />
                        </motion.button>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </GlassCard>

            {/* Frequency */}
            <GlassCard hover={false}>
              <label className="block text-sm font-medium text-gray-300 mb-4">
                Frequency
              </label>
              <FrequencySelector value={frequency} onChange={setFrequency} />
            </GlassCard>
          </div>

          {/* Settings Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Voice Settings */}
            <GlassCard hover={false}>
              <h3 className="text-sm font-medium text-gray-300 mb-4">Voice Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Voice</label>
                  <Select value={voice} onValueChange={setVoice}>
                    <SelectTrigger data-testid="voice-select" className="bg-black/20 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0f] border-white/10">
                      {voices.map((v) => (
                        <SelectItem key={v.value} value={v.value} className="text-white hover:bg-white/5">
                          <div className="flex items-center gap-2">
                            <Volume2 size={14} className="text-violet-400" />
                            <span>{v.label}</span>
                            <span className="text-xs text-gray-500">- {v.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-500">Speed</label>
                    <span className="text-xs text-violet-400 font-mono">{speed.toFixed(2)}x</span>
                  </div>
                  <Slider
                    value={[speed]}
                    onValueChange={([v]) => setSpeed(v)}
                    min={0.25}
                    max={1}
                    step={0.05}
                    data-testid="speed-slider"
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-gray-600 mt-1">
                    <span>Whisper</span>
                    <span>Normal</span>
                  </div>
                </div>
              </div>
            </GlassCard>

            {/* Loop Duration */}
            <GlassCard hover={false}>
              <h3 className="text-sm font-medium text-gray-300 mb-4">Loop Duration</h3>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500">Duration</label>
                  <span className="text-xs text-cyan-400 font-mono">{loopDuration} min</span>
                </div>
                <Slider
                  value={[loopDuration]}
                  onValueChange={([v]) => setLoopDuration(v)}
                  min={5}
                  max={120}
                  step={5}
                  data-testid="duration-slider"
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>5 min</span>
                  <span>2 hours</span>
                </div>
              </div>
            </GlassCard>

            {/* Generate Button */}
            <div className="space-y-3">
              <NeonButton
                onClick={handleGenerate}
                disabled={isGenerating}
                data-testid="generate-btn"
                className="w-full flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Generate Subliminal
                  </>
                )}
              </NeonButton>
              
              <NeonButton
                variant="ghost"
                onClick={handleReset}
                data-testid="reset-btn"
                className="w-full"
              >
                Reset
              </NeonButton>
            </div>
          </div>
        </div>

        {/* Generated Audio Player */}
        <AnimatePresence>
          {generatedAudio && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-8"
            >
              <AudioPlayer
                audioBase64={generatedAudio.audio_base64}
                title={generatedAudio.title}
                frequency={generatedAudio.frequency}
                loopEnabled={true}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Creator;
